// /groups/:groupId/leaderboard — wins, chops, and games played.
//
// The read side of what closing an event writes. Worth stating on the page,
// because it is a real behaviour change from the PHP version: a CANCELED event
// is excluded from games played. Before close_reason existed, calling off a game
// night still counted as a game played for every yes-RSVP, which quietly
// deflated everyone's win rate.

import { ApiClientError, fetchGroup, fetchLeaderboard } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import { GroupRole, type LeaderboardResponse } from '@msc/shared';

export const leaderboardView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  const yearParam = ctx.query.get('year');
  const year = yearParam === 'all' ? 'all' : yearParam && /^\d{4}$/.test(yearParam) ? Number(yearParam) : undefined;

  let board: LeaderboardResponse;
  let groupName: string;
  let canManage: boolean;
  let runsFasts: boolean;
  let showsBoard: boolean;
  try {
    const [group, data] = await Promise.all([fetchGroup(groupId), fetchLeaderboard(groupId, year)]);
    groupName = group.name;
    canManage = group.myRole >= GroupRole.ADMIN;
    runsFasts = groupRunsFasts(group.allowedEventTypes);
    showsBoard = groupShowsLeaderboard(group.allowedEventTypes);
    board = data;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the leaderboard.'));
    return;
  }

  const filters = el('nav', { class: 'subnav' }, [
    ...board.years.map((y) =>
      el(
        'a',
        {
          href: `/groups/${groupId}/leaderboard?year=${y}`,
          class: board.year === y ? 'active' : undefined,
        },
        String(y),
      ),
    ),
    el(
      'a',
      {
        href: `/groups/${groupId}/leaderboard?year=all`,
        class: board.year === null ? 'active' : undefined,
      },
      'All time',
    ),
  ]);

  const rows = board.entries.map((e, i) =>
    el('tr', {}, [
      el('td', {}, String(i + 1)),
      el('td', {}, e.displayName),
      el('td', {}, String(e.wins)),
      el('td', {}, String(e.chops)),
      el('td', {}, String(e.appearances)),
    ]),
  );

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/groups/${groupId}/events` }, `Back to ${groupName} events`)]),
    el('h1', {}, `🏆 Leaderboard · ${board.year === null ? 'All time' : board.year}`),
    groupSubnav(groupId, 'leaderboard', canManage, runsFasts, showsBoard),
    filters,
    rows.length
      ? el('div', { class: 'table-wrap' }, [
          el('table', { class: 'table' }, [
            el('thead', {}, [
              el(
                'tr',
                {},
                ['#', 'Player', 'Wins', 'Chops', 'Games played'].map((h) => el('th', {}, h)),
              ),
            ]),
            el('tbody', {}, rows),
          ]),
        ])
      : el('p', { class: 'muted' }, 'No results recorded yet.'),
    el(
      'p',
      { class: 'muted small' },
      'Games played counts a yes RSVP on any event that is over. Canceled events are excluded.',
    ),
  );
};
